import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LiveTag, TeamAvatar, fmtTime } from "@/components/common";
import { teamLabel } from "@/components/manage/matchUtils";
import ResultDialog from "@/components/manage/ResultDialog";
import { ClipboardEdit, Lock } from "lucide-react";

export default function ManageResults({ data, refresh, teamMap, liveOnly }) {
  const { matches } = data;
  const [active, setActive] = useState(null);

  const pool = liveOnly
    ? matches.filter((m) => m.status === "live" || (m.status === "pending" && m.team_a_id && m.team_b_id))
    : matches;
  const sorted = [...pool].sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));

  return (
    <div className="space-y-4">
      <h2 className="font-heading text-2xl font-bold">{liveOnly ? "Live control" : "Results"}</h2>
      {sorted.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">{liveOnly ? "No matches ready to play." : "No matches yet."}</Card>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {sorted.map((m) => {
            const isFinal = m.status === "final";
            const ready = m.team_a_id && m.team_b_id;
            return (
              <Card key={m.id} className="p-3" data-testid={`result-card-${m.id}`}>
                <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{m.round_name} · {m.field || "—"}</span>
                  {isFinal ? <span className="flex items-center gap-1 font-semibold text-emerald-600"><Lock className="h-3 w-3" /> FINAL</span>
                    : m.status === "live" ? <LiveTag /> : <span>{fmtTime(m.scheduled_time, m.timezone)}</span>}
                </div>
                <SideR m={m} side="a" teamMap={teamMap} win={isFinal && m.winner_id === m.team_a_id} />
                <SideR m={m} side="b" teamMap={teamMap} win={isFinal && m.winner_id === m.team_b_id} />
                {!isFinal && (
                  <Button size="sm" variant={m.status === "live" ? "default" : "outline"} className="mt-2 w-full"
                    disabled={!ready} onClick={() => setActive(m)} data-testid={`enter-result-${m.id}`}>
                    <ClipboardEdit className="mr-1.5 h-4 w-4" /> {m.status === "live" ? "Update / finish" : "Enter result"}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}
      <ResultDialog open={!!active} onOpenChange={() => setActive(null)} match={active} teamMap={teamMap} onDone={() => { refresh(); setActive(null); }} />
    </div>
  );
}

function SideR({ m, side, teamMap, win }) {
  const id = side === "a" ? m.team_a_id : m.team_b_id;
  const score = side === "a" ? m.score_a : m.score_b;
  return (
    <div className="flex items-center justify-between py-0.5">
      <div className="flex items-center gap-2">
        {id && teamMap[id] ? <TeamAvatar team={teamMap[id]} size={24} /> : <div className="h-6 w-6 rounded bg-muted" />}
        <span className={`text-sm ${win ? "font-bold" : ""}`}>{teamLabel(m, side, teamMap)}</span>
      </div>
      <span className="font-heading text-xl font-black">{score ?? "–"}</span>
    </div>
  );
}
